// 全局设备常量管理 - 登录后预加载所有设备常量
import { create } from 'zustand';
import { getDeviceConstants, clearDeviceConstantsCache, type DeviceConstants, type TemplateOption } from '../services/deviceConstants';

interface DeviceConstantsStore {
  constants: DeviceConstants | null;
  templateOptions: TemplateOption[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
  
  // Actions
  initialize: () => Promise<void>;
  refresh: () => Promise<void>;
  clearCache: () => void;
  reset: () => void;
}

export const useDeviceConstantsStore = create<DeviceConstantsStore>((set, get) => ({
  constants: null,
  templateOptions: [],
  loading: false,
  error: null,
  initialized: false,

  initialize: async () => {
    const state = get();
    
    // 如果已经初始化过且数据存在，直接返回
    if (state.initialized && state.constants) {
      return;
    }

    // 如果正在加载中，等待完成
    if (state.loading) {
      return new Promise((resolve) => {
        const checkLoading = () => {
          const currentState = get();
          if (!currentState.loading) {
            resolve();
          } else {
            setTimeout(checkLoading, 100);
          }
        };
        checkLoading();
      });
    }

    set({ loading: true, error: null });

    try {
      // 先尝试从 localStorage 读取
      console.log('🔧 正在初始化设备常量...');
      const data = await getDeviceConstants();
      console.log('✅ 设备常量加载成功:', data ? '数据已加载' : '无数据');
      set({ 
        constants: data, 
        templateOptions: data?.templates ?? [],
        loading: false, 
        error: null, 
        initialized: true 
      });
    } catch (error) {
      console.error('❌ 设备常量初始化失败:', error);
      set({ 
        loading: false, 
        error: error instanceof Error ? error.message : 'Failed to load device constants',
        initialized: false
      });
      // 不抛出错误，让页面能正常显示
      console.warn('设备常量加载失败，但继续执行');
    }
  },

  refresh: async () => {
    set({ loading: true, error: null });
    
    try {
      const data = await getDeviceConstants(true); // 强制刷新
      set({ 
        constants: data, 
        templateOptions: data?.templates ?? [],
        loading: false, 
        error: null, 
        initialized: true 
      });
    } catch (error) {
      set({ 
        loading: false, 
        error: error instanceof Error ? error.message : 'Failed to refresh device constants'
      });
      throw error;
    }
  },

  clearCache: () => {
    clearDeviceConstantsCache();
    set({ 
      constants: null, 
      templateOptions: [],
      initialized: false, 
      error: null 
    });
  },

  reset: () => {
    clearDeviceConstantsCache();
    set({ 
      constants: null, 
      templateOptions: [],
      loading: false, 
      error: null, 
      initialized: false 
    });
  }
}));

// 便捷的 Hook 用于组件中使用
export function useDeviceConstants() {
  const store = useDeviceConstantsStore();
  
  return {
    constants: store.constants,
    templateOptions: store.templateOptions,
    loading: store.loading,
    error: store.error,
    initialized: store.initialized,
    refresh: store.refresh,
    clearCache: store.clearCache
  };
}

// 登录后初始化设备常量的函数
export async function initializeDeviceConstantsOnLogin(): Promise<void> {
  const store = useDeviceConstantsStore.getState();
  await store.initialize();
}

// 登出时清理设备常量
export function clearDeviceConstantsOnLogout(): void {
  const store = useDeviceConstantsStore.getState();
  store.reset();
}
